/**
 * One session: who is on it, and what each side still owes.
 *
 * The activity screen answers "what is outstanding across this activity". This
 * answers the next question, which staff were previously reading between the
 * lines of a status badge: *who has to do what*.
 *
 * ---------------------------------------------------------------------------
 * Two panels, not one list
 * ---------------------------------------------------------------------------
 * A session has two sides — Parinaam's and the volunteers' — and which of them
 * owes anything is decided by `responsibility`. So the page is built as two
 * panels, each addressed to one side, each saying either what is left to do or
 * that there is nothing. A single merged checklist would make the reader work
 * out which items are theirs, which is the work this page exists to remove.
 *
 * The instructions are derived from the session's own state, never typed in.
 * A page that let somebody write the steps would drift from what the system
 * actually enforces, and the first time it disagreed it would be believed.
 *
 * ---------------------------------------------------------------------------
 * Where Parinaam's side is confirmed (14 Sep 2026)
 * ---------------------------------------------------------------------------
 * Here, on the panel that says it is owed. Until this button existed the panel
 * told a field coordinator to "confirm Parinaam's side" and gave them nothing
 * to press: the API took the confirmation from any staff account, but the only
 * screen that called it was the volunteer's own `/my-volunteering`, which the
 * staff guard bounces them out of. The work was reachable only by an admin
 * overriding the status — which records something else entirely, that somebody
 * overruled the answer rather than that the work was done.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CheckboxGroup,
  DeletePanel,
  DurationInput,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SelectInput,
  StatusBadge,
  TextArea,
  TextInput,
  useToast,
} from '../../components/common';
import type { SelectOption } from '../../components/common';
import { CRUMB } from '../../constants/breadcrumbs';
import {
  useConfirmSession,
  useDeleteSession,
  useSession,
  useSessionRoster,
  useSetSessionCapacity,
  useSetSessionRepresentative,
  useUpdateSession,
} from '../../hooks/useSessions';
import { useAuth } from '../../hooks/useAuth';
import { useCommunities } from '../../hooks/useCommunities';
import { useStaffAccounts } from '../../hooks/useStaffAccounts';
import type { Session } from '../../services/api/sessions';
import { formatDuration } from '@parinaam-vms/shared';
import { formatDate, formatTimeRange, humaniseCode, orDash } from '../../utils/format';
import styles from './SessionDetailPage.module.css';

/** A session over a range reads as a range; a one-day session reads as a day. */
function whenText(session: Session): string {
  if (!session.end_date || session.end_date === session.start_date) {
    return formatDate(session.start_date);
  }
  return `${formatDate(session.start_date)} – ${formatDate(session.end_date)}`;
}

/** Whether a side owes this session anything at all. */
function owes(session: Session, side: 'PARINAAM' | 'VOLUNTEER'): boolean {
  return session.responsibility === 'COLLABORATIVE' || session.responsibility === side;
}

export function SessionDetailPage() {
  const { hasRole } = useAuth();
  const { id } = useParams<{ id: string }>();
  const sessionId = id as string;

  const session = useSession(sessionId);
  const roster = useSessionRoster(sessionId);

  if (session.isPending) return <LoadingState label="Loading the session…" />;
  if (session.isError) {
    return <ErrorState error={session.error} onRetry={() => void session.refetch()} />;
  }

  const it = session.data;

  return (
    <div className={styles.page}>
      {/*
        The trail is the chain this session sits in, and it is the whole reason
        the page can be reached by URL and still make sense: a session name like
        "Q3 · Sudhama Nagar" says nothing without the activity above it.
      */}
      <PageHeader
        title={it.session_name}
        description={`${it.program_name} · ${humaniseCode(it.status)}`}
        breadcrumbs={[
          CRUMB.activities,
          { label: it.activity_name, to: `/activities/${it.activity_id}` },
          { label: it.session_name },
        ]}
      />

      <Card>
        <dl className={styles.facts}>
          <div>
            <dt>Session name</dt>
            <dd>{it.session_name}</dd>
          </div>
          <div>
            <dt>Session date</dt>
            <dd>{whenText(it)}</dd>
          </div>
          <div>
            <dt>Session time</dt>
            <dd>{formatTimeRange(it.start_time, it.end_time)}</dd>
          </div>
          <div>
            <dt>Session location</dt>
            <dd>{orDash(it.location)}</dd>
          </div>
          <div>
            <dt>Number of hours per session</dt>
            <dd>{it.planned_minutes == null ? '—' : formatDuration(it.planned_minutes)}</dd>
          </div>
          <div>
            <dt>Number of volunteers per session</dt>
            <dd>
              {it.volunteers_needed == null
                ? it.assigned_count
                : `${it.assigned_count} of ${it.volunteers_needed}`}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <StatusBadge status={it.status} />
            </dd>
          </div>
          <div className={styles.factWide}>
            <dt>Session description</dt>
            <dd>{orDash(it.description)}</dd>
          </div>
        </dl>

        {it.status_override && it.override_reason && (
          <p className={styles.override}>
            An admin set this status. <strong>Reason:</strong> {it.override_reason}
          </p>
        )}
      </Card>

      <EditSessionPanel session={it} />

      {/*
        Admin only, and at the foot of the page rather than beside the edit
        control: a destructive action should not sit where somebody reaching for
        "fix the name" can hit it.
      */}
      {hasRole('ADMIN') && <DeleteSessionPanel session={it} />}

      {/*
        Parinaam first, then the volunteers. Not alphabetical and not arbitrary:
        this screen is read by staff, and the panel addressed to them is the one
        they can act on without picking up a phone.
      */}
      <div className={styles.sides}>
        <ParinaamSide session={it} />
        <VolunteerSide session={it} volunteerCount={roster.data?.length ?? 0} />
      </div>

      <Card title="Volunteers on this session">
        {roster.isPending && <LoadingState label="Loading the roster…" />}
        {roster.isError && (
          <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
        )}
        {roster.data?.length === 0 && (
          <EmptyState
            title="Nobody is assigned yet"
            message="Volunteers are added from the activity screen."
          />
        )}
        {roster.data && roster.data.length > 0 && (
          <ul className={styles.roster}>
            {roster.data.map((row) => (
              <li key={row.assignment_id} className={styles.rosterRow}>
                {/*
                  Code first, then the name.

                  It is the column staff read down when they have a code in
                  hand — off an attendance sheet or a WhatsApp message — and a
                  code that starts each row is scannable in a way one tucked
                  after a name of unpredictable length is not.
                */}
                <span className={styles.rosterCode}>{row.volunteer_code}</span>
                <span className={styles.rosterWho}>
                  <Link className={styles.rosterName} to={`/volunteers/${row.volunteer_id}`}>
                    {row.volunteer_name}
                  </Link>
                  {/*
                    Whether they are here on their own or through an employer,
                    and which. It decides what the attendance form asks for — a
                    corporate row can carry a group headcount — so a roster that
                    does not say it leaves staff to remember.
                  */}
                  <span className={styles.rosterKind}>
                    {humaniseCode(row.volunteer_category)}
                    {row.volunteer_organization_name && ` · ${row.volunteer_organization_name}`}
                  </span>
                </span>
                <StatusBadge status={row.assignment_status} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * What Parinaam owes, and who owes it.
 *
 * The representative is editable here rather than on the activity screen
 * because it is a fact about one session — a coordinator runs the Tuesday
 * group, not the program.
 */
function ParinaamSide({ session }: { session: Session }) {
  const staff = useStaffAccounts();
  const setRep = useSetSessionRepresentative();
  const confirm = useConfirmSession();
  const toast = useToast();
  const [asking, setAsking] = useState(false);

  const due = owes(session, 'PARINAAM');
  const done = session.parinaam_confirmed_at !== null;

  /*
   * Offered only when this side genuinely owes a confirmation nobody has given.
   *
   * The status check is not implied by the other two. An admin can override a
   * session to COMPLETED without either side confirming, which leaves this
   * panel owing something on a session that is finished — and the API answers
   * 409 to a confirmation on it. A button that exists to produce an error is
   * worse than no button.
   */
  const mayConfirm = due && !done && session.status !== 'COMPLETED';

  const doConfirm = () => {
    confirm.mutate(session.session_id, {
      onSuccess: (result) => {
        setAsking(false);
        toast.success(
          result.completed
            ? `${session.session_name} is now complete.`
            : 'Recorded. The volunteers still have to confirm their side.'
        );
      },
      onError: (error) => toast.error(error, 'Could not record the confirmation.'),
    });
  };

  const change = (userId: string) => {
    setRep.mutate(
      { sessionId: session.session_id, userId: userId || null },
      {
        onSuccess: () => toast.success('The Parinaam representative has been updated.'),
        onError: (error) => toast.error(error, 'Could not set the representative.'),
      }
    );
  };

  return (
    <Card title="Parinaam's side">
      <div className={styles.who}>
        <SelectInput
          label="Responsible"
          placeholder="Nobody named yet"
          options={(staff.data ?? []).map((person) => ({
            value: person.user_id,
            label: `${person.display_name} — ${humaniseCode(person.role)}`,
          }))}
          value={session.parinaam_rep_user_id ?? ''}
          onChange={(event) => change(event.target.value)}
          disabled={setRep.isPending || staff.isPending}
        />
      </div>

      <ActionList
        due={due}
        done={done}
        nothingToDo='The volunteers complete this session.'
        completed={
          session.parinaam_confirmed_by
            ? `Confirmed by ${session.parinaam_confirmed_by}.`
            : 'Confirmed.'
        }
        steps={[
          'Record attendance for everyone who came, on the day they came.',
          'Approve those hours, or send them back with a reason.',
          'Confirm Parinaam’s side once the work is done.',
        ]}
      />

      {mayConfirm &&
        (asking ? (
          <div className={styles.confirmBox}>
            {/*
              Asked once, the same way the volunteer's own screen asks.
              A confirmation is a statement of fact about work that was done,
              and the person making it cannot take it back — only an admin
              reopening the session can.
            */}
            <p className={styles.confirmAsk}>
              Confirm that Parinaam’s part of this session is finished?
            </p>
            <div className={styles.confirmActions}>
              <Button variant="primary" onClick={doConfirm} disabled={confirm.isPending}>
                {confirm.isPending ? 'Confirming…' : 'Yes, it is done'}
              </Button>
              <Button onClick={() => setAsking(false)} disabled={confirm.isPending}>
                Not yet
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.confirmActions}>
            <Button variant="primary" onClick={() => setAsking(true)}>
              Confirm Parinaam’s side
            </Button>
          </div>
        ))}
    </Card>
  );
}

/**
 * What the volunteers owe.
 *
 * No picker: the volunteer side is owed by whoever is on the roster, and the
 * roster is below. Naming one of them would be inventing a spokesperson the
 * model does not have — any assigned volunteer may confirm.
 */
function VolunteerSide({
  session,
  volunteerCount,
}: {
  session: Session;
  volunteerCount: number;
}) {
  const due = owes(session, 'VOLUNTEER');
  const done = session.volunteer_confirmed_at !== null;

  return (
    <Card title="The volunteers' side">
      <p className={styles.who}>
        {volunteerCount === 0
          // Not "nobody is assigned" — the roster below says that. This says
          // what it MEANS for this side: there is nobody who could confirm it.
          ? 'No volunteers are assigned, so nobody can confirm this side yet.'
          : `${volunteerCount} ${volunteerCount === 1 ? 'volunteer' : 'volunteers'} assigned. Any of them may confirm.`}
      </p>

      <ActionList
        due={due}
        done={done}
        nothingToDo='Parinaam completes this session.'
        completed={
          session.volunteer_confirmed_by
            ? `Confirmed by ${session.volunteer_confirmed_by}.`
            : 'Confirmed.'
        }
        steps={[
          'Attend the session.',
          'Confirm it from “My volunteering” once it is done.',
        ]}
      />
    </Card>
  );
}

/**
 * One side's instructions, in the three states a side can be in.
 *
 * Shared so the two panels cannot drift into saying the same thing differently
 * — the whole value of this screen is that the two halves are directly
 * comparable.
 */
function ActionList({
  due,
  done,
  steps,
  nothingToDo,
  completed,
}: {
  due: boolean;
  done: boolean;
  steps: string[];
  nothingToDo: string;
  completed: string;
}) {
  if (!due) return <p className={styles.settled}>{nothingToDo}</p>;
  if (done) return <p className={styles.settled}>{completed}</p>;

  return (
    <>
      <p className={styles.outstanding}>Still to do</p>
      <ol className={styles.steps}>
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </>
  );
}

/**
 * Correcting the session, and removing one that should not exist.
 *
 * Both arrive on this page rather than on the activity's session list, for the
 * reason the activity's own edit form is on the activity page: the screen that
 * shows you a record is the screen you fix it from, and a list with an edit
 * control per row is a list that is mostly controls.
 *
 * The split in who may do what is deliberate. Staff edit; only an admin
 * deletes. Editing a name or a date is maintenance, and a coordinator
 * maintaining a roster needs it. Deleting cannot be undone, and the narrow case
 * it exists for — a session added by mistake, before anybody was assigned — is
 * rare enough not to need to be within everyone's reach.
 */
function EditSessionPanel({ session }: { session: Session }) {
  const update = useUpdateSession();
  const capacity = useSetSessionCapacity();
  const communities = useCommunities();
  const toast = useToast();
  const [editing, setEditing] = useState(false);

  const [form, setForm] = useState({
    name: session.session_name,
    start_date: session.start_date,
    end_date: session.end_date ?? '',
    start_time: session.start_time ?? '',
    end_time: session.end_time ?? '',
    location: session.location ?? '',
    description: session.description ?? '',
  });
  const [plannedMinutes, setPlannedMinutes] = useState(session.planned_minutes ?? 0);

  /*
   * The two fields the create form asks for that are not plain text.
   *
   * Communities ride on the PATCH, which already takes them. The cap does not:
   * it has its own endpoint and its own audit meaning, the same way it does on
   * the activity edit form.
   */
  const [communityIds, setCommunityIds] = useState<string[]>(session.community_ids ?? []);
  const [volunteersNeeded, setVolunteersNeeded] = useState(
    session.volunteers_needed == null ? '' : String(session.volunteers_needed)
  );

  const communityOptions: SelectOption[] = (communities.data ?? []).map((community) => ({
    value: community.community_id,
    label: community.city ? `${community.name} — ${community.city}` : community.name,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const problems: Record<string, string> = {};
    if (form.name.trim() === '') problems.name = 'A session needs a name.';
    if (form.start_date === '') problems.start_date = 'When does this session start?';
    if (form.end_date && form.end_date < form.start_date) {
      problems.end_date = 'The end date must be on or after the start date.';
    }
    // The same rule the API and `sessions_time_check` apply (§8.40), said here
    // so staff learn it without a round trip.
    if (form.start_time && form.end_time && form.start_time >= form.end_time) {
      problems.end_time = 'The end time must be after the start time.';
    }
    // The floor the API and the create form both apply: a session serves
    // somebody, or it is not a session.
    if (communityIds.length === 0) {
      problems.community_ids = 'Choose at least one community this session serves.';
    }
    if (volunteersNeeded && (!/^\d+$/.test(volunteersNeeded) || Number(volunteersNeeded) < 1)) {
      problems.volunteers_needed = 'Enter a whole number of at least 1, or leave it blank.';
    }
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const next: Record<string, string | null> = {
      name: form.name.trim(),
      start_date: form.start_date,
      end_date: form.end_date || null,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      location: form.location.trim() || null,
      description: form.description.trim() || null,
    };

    // Only what moved. PATCH treats an absent key as "leave alone" and a null as
    // "clear", so sending the whole form would rewrite fields nobody touched.
    const stored: Record<string, string | null> = {
      name: session.session_name,
      start_date: session.start_date,
      end_date: session.end_date,
      start_time: session.start_time,
      end_time: session.end_time,
      location: session.location,
      description: session.description,
    };
    const changes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(next)) {
      if (value !== (stored[key] ?? null)) changes[key] = value;
    }
    if (plannedMinutes !== (session.planned_minutes ?? 0)) {
      changes.planned_minutes = plannedMinutes;
    }

    // Order-insensitive: ticking the same two communities in the other order is
    // not a change, and sending it would write an audit row saying it was.
    const stored_communities = session.community_ids ?? [];
    const sameCommunities =
      communityIds.length === stored_communities.length &&
      [...communityIds].sort().join() === [...stored_communities].sort().join();
    if (!sameCommunities) changes.community_ids = communityIds;

    const wantedCap = volunteersNeeded === '' ? null : Number(volunteersNeeded);
    const capChanged = wantedCap !== (session.volunteers_needed ?? null);

    if (Object.keys(changes).length === 0 && !capChanged) {
      toast.info('No changes to save.');
      setEditing(false);
      return;
    }

    /*
     * The session first, then the cap, then close once — sequential rather than
     * fired together, so a refusal on one does not leave the panel shut with
     * the other half applied.
     */
    void (async () => {
      try {
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ sessionId: session.session_id, changes });
        }
        if (capChanged) {
          await capacity.mutateAsync({
            sessionId: session.session_id,
            volunteersNeeded: wantedCap,
          });
        }
        toast.success('Session updated.');
        setEditing(false);
      } catch (error) {
        toast.error(error, 'Could not update the session.');
      }
    })();
  };

  if (!editing) {
    return (
      <Card
        title="This session"
        action={
          <Button variant="primary" onClick={() => setEditing(true)}>
            Edit session
          </Button>
        }
      >
        <p className={styles.hint}>
          Correct the details this session was created with.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Edit session">
      <form onSubmit={handleSubmit} noValidate>
        <fieldset className={styles.form} disabled={update.isPending || capacity.isPending}>
          <TextInput
            label="Session name"
            required
            value={form.name}
            error={errors.name}
            onChange={(event) => set('name')(event.target.value)}
          />

          {communities.isPending ? (
            <LoadingState label="Loading communities…" />
          ) : (
            <CheckboxGroup
              label="Which communities does this session serve?"
              options={communityOptions}
              value={communityIds}
              error={errors.community_ids}
              onChange={(next) => {
                setCommunityIds(next);
                setErrors((current) => ({ ...current, community_ids: '' }));
              }}
            />
          )}

          <div className={styles.row}>
            <TextInput
              label="Session start date"
              type="date"
              required
              value={form.start_date}
              error={errors.start_date}
              onChange={(event) => set('start_date')(event.target.value)}
            />
            <TextInput
              label="Session end date"
              type="date"
              value={form.end_date}
              error={errors.end_date}
              onChange={(event) => set('end_date')(event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="Session start time"
              type="time"
              value={form.start_time}
              error={errors.start_time}
              onChange={(event) => set('start_time')(event.target.value)}
            />
            <TextInput
              label="Session end time"
              type="time"
              value={form.end_time}
              error={errors.end_time}
              onChange={(event) => set('end_time')(event.target.value)}
            />
          </div>

          <TextInput
            label="Session location"
            value={form.location}
            onChange={(event) => set('location')(event.target.value)}
          />

          <DurationInput
            label="Number of hours per session"
            value={plannedMinutes}
            onChange={setPlannedMinutes}
          />

          <TextInput
            label="Number of volunteers per session"
            type="number"
            min={1}
            placeholder="Leave blank for no limit"
            value={volunteersNeeded}
            error={errors.volunteers_needed}
            onChange={(event) => {
              setVolunteersNeeded(event.target.value);
              setErrors((current) => ({ ...current, volunteers_needed: '' }));
            }}
          />

          <TextArea
            label="Session description"
            value={form.description}
            onChange={(event) => set('description')(event.target.value)}
          />
        </fieldset>

        <div className={styles.actions}>
          <Button type="submit" variant="primary" loading={update.isPending || capacity.isPending}>
            Save
          </Button>
          <Button onClick={() => setEditing(false)} disabled={update.isPending || capacity.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Removing a session, for an admin only.
 *
 * The API refuses one with a roster or hours against it (§5.8 — those are
 * cancelled, never removed), so the consequences below say what the panel can
 * promise rather than what it hopes: nothing is lost, because nothing has been
 * recorded yet.
 */
function DeleteSessionPanel({ session }: { session: Session }) {
  const navigate = useNavigate();
  const remove = useDeleteSession();
  const toast = useToast();

  return (
    <DeletePanel
      title="Delete this session"
      noun="session"
      deleted={false}
      pending={remove.isPending}
      error={remove.error}
      consequences={
        <>
          Only an unused session can be deleted.
        </>
      }
      onDelete={(reason) =>
        remove.mutate(
          { sessionId: session.session_id, reason },
          {
            onSuccess: () => {
              toast.success('Session deleted.');
              navigate(`/activities/${session.activity_id}`);
            },
            onError: (error) => toast.error(error, 'Could not delete the session.'),
          }
        )
      }
    />
  );
}
